import { useState, useEffect, useRef, useCallback } from "react";

const STORAGE_PREFIX = "vcpl_draft_";

// Drafts belong to the signed-in user: the key is namespaced by user so a
// shared machine never offers one person's unsaved voucher to the next. They
// deliberately SURVIVE logout — an expired session dispatches logout too, and
// restoring the half-typed voucher after signing back in is the whole point.
// Stale drafts expire instead (DRAFT_TTL_MS).
export const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000;
let draftOwner = "anon";
export function setDraftOwner(id) {
    draftOwner = id == null || id === "" ? "anon" : String(id);
}
// Drop drafts older than the TTL (any user). Called once at startup.
export function sweepExpiredDrafts(now = Date.now()) {
    try {
        Object.keys(localStorage)
            .filter((k) => k.startsWith(STORAGE_PREFIX))
            .forEach((k) => {
                try {
                    const savedAt = JSON.parse(localStorage.getItem(k))?._meta?.savedAt;
                    if (!savedAt || now - savedAt > DRAFT_TTL_MS) localStorage.removeItem(k);
                } catch {
                    localStorage.removeItem(k);
                }
            });
    } catch { /* storage unavailable */ }
}

export function clearAllDrafts() {
    try {
        Object.keys(localStorage)
            .filter((k) => k.startsWith(STORAGE_PREFIX))
            .forEach((k) => localStorage.removeItem(k));
    } catch { /* storage unavailable */ }
}

// How many mounted forms are autosaving right now. While one is, leaving the
// page loses nothing (the draft is restored on return), so FormActions skips
// its unsaved-changes prompt.
let activeAutoSaves = 0;
export const isAutoSaveActive = () => activeAutoSaves > 0;

export function getTimeAgo(ts) {
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

function useAutoSave({ key, data, debounceMs = 2000, enabled = true, version = 1, hasContent: hasContentFn }) {
    const storageKey = `${STORAGE_PREFIX}${draftOwner}_${key}`;
    // While the "restore earlier draft?" question is unanswered, new work goes to a pending
    // slot instead of nowhere (it used to be dropped: a scan session done without answering
    // the toast was lost on refresh). On the next visit the newer of the two is offered.
    const pendingKey = `${storageKey}__pending`;
    const timerRef = useRef(null);
    const dataRef = useRef(data);
    const enabledRef = useRef(enabled);

    useEffect(() => {
        if (!enabled) return undefined;
        activeAutoSaves += 1;
        return () => { activeAutoSaves -= 1; };
    }, [enabled]);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [draft, setDraft] = useState(() => {
        try {
            const pendingRaw = localStorage.getItem(pendingKey);
            if (pendingRaw) {
                const mainAt = JSON.parse(localStorage.getItem(storageKey) || "null")?._meta?.savedAt ?? 0;
                const pendingAt = JSON.parse(pendingRaw)?._meta?.savedAt ?? 0;
                if (pendingAt >= mainAt) localStorage.setItem(storageKey, pendingRaw);
                localStorage.removeItem(pendingKey);
            }
            const raw = localStorage.getItem(storageKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            const tooOld = !parsed?._meta?.savedAt || Date.now() - parsed._meta.savedAt > DRAFT_TTL_MS;
            if (parsed?._meta?.version !== version || tooOld) {
                localStorage.removeItem(storageKey);
                return null;
            }
            return parsed;
        } catch {
            return null;
        }
    });

    dataRef.current = data;
    enabledRef.current = enabled;

    const hasDraft = draft !== null;
    const hasDraftRef = useRef(hasDraft);
    hasDraftRef.current = hasDraft;
    // The form as it stood when autosave switched on (loaded document, defaults). Only work
    // beyond it is "pending"; otherwise an untouched page would outrank the real draft.
    const baselineRef = useRef(null);
    useEffect(() => {
        if (enabled && baselineRef.current === null) baselineRef.current = JSON.stringify(data ?? null);
    }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps
    const draftData = draft?.data ?? null;
    const draftSavedAt = draft?._meta?.savedAt ?? null;

    const hasContentFnRef = useRef(hasContentFn);
    hasContentFnRef.current = hasContentFn;

    const writeToStorage = useCallback(() => {
        const d = dataRef.current;
        if (!d) return;
        const checkFn = hasContentFnRef.current;
        const hasContent = checkFn ? checkFn(d) : Object.values(d).some((v) =>
            Array.isArray(v) ? v.length > 0 : v !== "" && v !== null && v !== undefined
        );
        if (!hasContent) return;
        // Nothing differs from the form as it loaded: no draft. (Writing one anyway
        // made every opened edit page say "Unsaved changes found" next time, and
        // Restore then put an old copy over newer server data.) If this session
        // already wrote a draft and the user changed things back, drop it.
        if (JSON.stringify(d) === baselineRef.current) {
            if (!hasDraftRef.current) {
                try { localStorage.removeItem(storageKey); } catch { /* storage unavailable */ }
            }
            return;
        }
        const target = hasDraftRef.current ? pendingKey : storageKey;
        try {
            const envelope = {
                _meta: { savedAt: Date.now(), version },
                data: d,
            };
            localStorage.setItem(target, JSON.stringify(envelope));
            setLastSavedAt(Date.now());
        } catch (e) {
            console.warn("Auto-save failed:", e);
        }
    }, [storageKey, pendingKey, version]);

    // An edit whose debounced write hasn't run yet. Leaving the page inside the
    // debounce window used to drop it (unmount only cleared the timer); it is
    // now flushed on unmount — unless the page cleared the draft after saving.
    const unsavedEditRef = useRef(false);
    useEffect(() => {
        if (!enabledRef.current) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        unsavedEditRef.current = true;
        timerRef.current = setTimeout(() => {
            unsavedEditRef.current = false;
            writeToStorage();
        }, debounceMs);
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [data, debounceMs, hasDraft, writeToStorage]);

    const writeRef = useRef(writeToStorage);
    writeRef.current = writeToStorage;
    useEffect(() => () => {
        // Only real edits: an untouched page re-renders with an equal object.
        if (!unsavedEditRef.current || !enabledRef.current) return;
        if (JSON.stringify(dataRef.current ?? null) === baselineRef.current) return;
        writeRef.current();
    }, []);

    useEffect(() => {
        const flushOnExit = () => {
            if (enabledRef.current) writeToStorage();
        };
        const handleVisibility = () => {
            if (document.visibilityState === "hidden") flushOnExit();
        };
        window.addEventListener("beforeunload", flushOnExit);
        document.addEventListener("visibilitychange", handleVisibility);
        return () => {
            window.removeEventListener("beforeunload", flushOnExit);
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [writeToStorage]);

    const discardDraft = useCallback(() => {
        // The work done while the question was open becomes the draft.
        const pending = localStorage.getItem(pendingKey);
        if (pending) localStorage.setItem(storageKey, pending);
        else localStorage.removeItem(storageKey);
        localStorage.removeItem(pendingKey);
        setDraft(null);
    }, [storageKey, pendingKey]);

    const clearDraft = useCallback(() => {
        unsavedEditRef.current = false; // saved: nothing to flush on the way out
        localStorage.removeItem(storageKey);
        localStorage.removeItem(pendingKey);
        setDraft(null);
        setLastSavedAt(null);
    }, [storageKey, pendingKey]);

    const restoreDraft = useCallback(() => {
        const d = draft?.data ?? null;
        localStorage.removeItem(pendingKey); // the restored draft replaces what was on screen
        setDraft(null);
        return d;
    }, [draft, pendingKey]);

    return { hasDraft, draftData, draftSavedAt, restoreDraft, discardDraft, clearDraft, lastSavedAt };
}

export default useAutoSave;
